import React from 'react';
import { Copy, CheckCheck } from 'lucide-react';
import { useCopy } from '../../contexts/CopyContext';

const CopyableValue = ({
    value,
    label,
    size = 'default',
    className = '',
    preserveFormatting = false,
    iconClassName = '',
    children,
    showTooltip = false,
    afterElement = null,
    copyEnabled = true
}) => {
    const { copiedField, tooltipPosition, handleCopy } = useCopy();

    const handleCopyClick = (event) => {
        if (!copyEnabled) return;

        event.stopPropagation();
        const cleanValue = String(value)
            .trim()
            // Remove any text before numbers (including '$' and text like 'Margin: ')
            .replace(/^[^0-9.-]*/, '')
            // Extract the full number with optional decimal part
            .match(/([-]?\d+(?:,\d+)*(?:\.\d+)?)/)?.[0] || '';

        // Remove commas before copying
        const finalValue = cleanValue.replace(/,/g, '');

        if (finalValue) {
            const copyToClipboard = async (text) => {
                if (navigator.clipboard && window.isSecureContext) {
                    // For HTTPS or localhost
                    await navigator.clipboard.writeText(text);
                } else {
                    // Fallback for other contexts
                    const textArea = document.createElement('textarea');
                    textArea.value = text;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    textArea.style.top = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    try {
                        document.execCommand('copy');
                    } catch (error) {
                        console.error('Error copying text: ', error);
                    }
                    textArea.remove();
                }
            };

            copyToClipboard(finalValue)
                .then(() => {
                    if (showTooltip) {
                        // Calculate tooltip position based on element position
                        if (event && event.currentTarget) {
                            const rect = event.currentTarget.getBoundingClientRect();
                            const viewportWidth = window.innerWidth;
                            const elementCenterX = rect.left + rect.width / 2;

                            let position = 'center';
                            if (elementCenterX > viewportWidth - 70) {
                                position = 'right';
                            }
                            else if (elementCenterX < 70) {
                                position = 'left';
                            }
                            handleCopy(label, position);
                        } else {
                            handleCopy(label);
                        }
                    } else {
                        handleCopy(label);
                    }
                })
                .catch(error => {
                    console.error('Failed to copy: ', error);
                });
        }
    };

    const sizeClasses = {
        'large': 'text-4xl',
        'default': 'text-2xl',
        'small': 'text-sm'
    };

    const iconSize = size === 'large' ? 18 : 12;

    const renderCopyButton = () => {
        if (!copyEnabled) return null;

        return (
            <div
                className={`relative ml-1 flex items-center h-[1.25em] ${iconClassName}`}
                aria-label={`Copy ${label}`}
            >
                {copiedField === label ? (
                    <span className="relative inline-flex items-center h-full">
                        <CheckCheck size={iconSize} className="text-emerald-500" />
                        {showTooltip ? (
                            <div className="absolute bottom-full mb-1 text-emerald-500 text-xs bg-emerald-950 px-1.5 py-0.5 rounded select-none pointer-events-none whitespace-nowrap font-normal" style={{
                                left: tooltipPosition === 'right' ? 'auto' : '50%',
                                right: tooltipPosition === 'right' ? '0' : 'auto',
                                transform: tooltipPosition === 'left' || tooltipPosition === 'right' ? 'none' : 'translateX(-50%)'
                            }}>
                                Copied
                                <div className="absolute top-full border-l-4 border-r-4 border-t-4 border-b-0 border-l-transparent border-r-transparent border-emerald-950" style={{
                                    height: '4px',
                                    width: '8px',
                                    left: tooltipPosition === 'right' ? 'calc(100% - 10px)' :
                                        tooltipPosition === 'left' ? '10px' : '50%',
                                    transform: tooltipPosition === 'left' || tooltipPosition === 'right' ? 'none' : 'translateX(-50%)'
                                }}></div>
                            </div>
                        ) : (
                            <span className="ml-1 text-emerald-500 text-xs font-normal">Copied</span>
                        )}
                    </span>
                ) : (
                    <Copy
                        size={iconSize}
                        className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-slate-300 transition-all"
                    />
                )}
            </div>
        );
    };

    return (
        <button
            onClick={handleCopyClick}
            className={`group inline-flex items-center w-full text-left ${className} ${copyEnabled ? 'cursor-pointer' : ''} bg-transparent border-none p-0 m-0 focus:outline-none leading-none`}
        >
            {children || (
                <span className={`${preserveFormatting ? '' : `${sizeClasses[size]} tabular-nums`} leading-none`}>
                    {value}
                </span>
            )}
            {afterElement}
            {renderCopyButton()}
        </button>
    );
};

export default CopyableValue; 